import type { RootState } from '@/store';
import type { Workspace } from '@web/types';
import type { WorkspacePermissionActionGrant } from '@web/types';

export const WORKSPACE_PERMISSION_RESOURCES = [
  'workspace_invite',
  'workspace_management',
  'service_type_management',
  'deliverable_type_management',
  'contact_management',
  'contact_import',
  'workspace_member_management',
  'task_management',
  'job_management',
  'job_transition_management',
  'change_order_management',
  'change_order_apply',
  'task_assignee_management',
  'time_tracking',
  'time_tracking_management',
  'see_others_performance',
  'list_management',
  'plan_management',
  'manage_payment_information',
  'manage_own_messages',
  'set_own_messages_context_tags',
  'set_other_messages_context_tags',
] as const;

export type WorkspacePermissionResource =
  (typeof WORKSPACE_PERMISSION_RESOURCES)[number];

export const WORKSPACE_PERMISSION_ACTIONS = [
  'view',
  'create',
  'edit',
  'delete',
] as const;

export type WorkspacePermissionAction =
  (typeof WORKSPACE_PERMISSION_ACTIONS)[number];

type WorkspacePermissionEntry = {
  actions?: Partial<
    Record<
      WorkspacePermissionAction,
      boolean | WorkspacePermissionActionGrant | undefined
    >
  >;
};
type WorkspacePermissions = Partial<
  Record<WorkspacePermissionResource, WorkspacePermissionEntry | undefined>
>;

function asWorkspacePermissions(
  permissions: Workspace['permissions'] | undefined,
): WorkspacePermissions | undefined {
  if (!permissions) {
    return undefined;
  }

  return permissions as unknown as WorkspacePermissions;
}

export function getWorkspaceActionGrant(
  workspace: Workspace | null | undefined,
  resource: WorkspacePermissionResource,
  action: WorkspacePermissionAction,
  fallback = false,
): WorkspacePermissionActionGrant {
  const permissions = asWorkspacePermissions(workspace?.permissions);
  const value = permissions?.[resource]?.actions?.[action];

  if (value === undefined) {
    return { permission: fallback, scopes: [] };
  }

  if (typeof value === 'boolean') {
    return { permission: value, scopes: [] };
  }

  if (
    value &&
    typeof value.permission === 'boolean' &&
    Array.isArray(value.scopes) &&
    value.scopes.every((scope) => typeof scope === 'string' && scope.length > 0)
  ) {
    return {
      permission: value.permission,
      scopes: value.permission ? [...value.scopes] : [],
    };
  }

  return { permission: false, scopes: [] };
}

export function canWorkspaceAction(
  workspace: Workspace | null | undefined,
  resource: WorkspacePermissionResource,
  action: WorkspacePermissionAction,
  fallback = false,
): boolean {
  return getWorkspaceActionGrant(workspace, resource, action, fallback)
    .permission;
}

export function selectCanWorkspaceAction(
  state: RootState,
  resource: WorkspacePermissionResource,
  action: WorkspacePermissionAction,
  fallback = false,
): boolean {
  return canWorkspaceAction(
    state.workspaces.currentWorkspace,
    resource,
    action,
    fallback,
  );
}
